import { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Button } from '../../components/shared/FormElements';
import { GroupType } from '../../types';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { setLoading, setPopAlert } from '../../slices/miscSlice';
import { setGroups } from '../../slices/permissionsSlice';
import { useAppContext } from '../../Context';
import { constants } from '../../constants';
import '../../styles/DataTable.css';
import { usePermissions } from '../../services';

const ViewRoles: React.FC = () => {
  const [addNewForm, setAddNewForm] = useState(false);
  const [editForm, setEditForm] = useState(false);
  const [newRoleName, setNewRoleName] = useState('' as string);
  const [editRole, setEditRole] = useState({} as GroupType);
  const [selectedPermissions, setSelectedPermissions] = useState<Array<string>>([]);
  const [permissionOptions, setPermissionOptions] = useState<Array<{ permission: string; label: string }>>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const { groups } = useAppSelector((state) => state.permissions);
  const dispatch = useAppDispatch();
  const { apiCall } = useAppContext();
  const { canView_group, canAdd_group, canChange_group, canDelete_group } = usePermissions([
    'view_group',
    'add_group',
    'change_group',
    'delete_group',
  ]);

  useEffect(() => {
    if (!canView_group) return;
    if (permissionsLoaded) return;
    const normalizeCode = (code: string) => code.trim();
    const resolveLabel = (code: string, name?: string) =>
      constants.permission_labels[code] ||
      constants.permission_labels[code.toLowerCase()] ||
      name ||
      code;
    const loadPermissions = async () => {
      const response = await apiCall('GET', 'permissions/catalog/', null, '', 'Error al obtener los permisos');
      if (response?.permissions) {
        const options = response.permissions
          .map((permission: { codename?: string; name?: string }) => {
            const codename = normalizeCode(permission.codename || '');
            if (!codename) return null;
            const label = resolveLabel(codename, permission.name);
            return { permission: codename, label };
          })
          .filter(Boolean) as Array<{ permission: string; label: string }>;
        setPermissionOptions(options.sort((a, b) => a.label.localeCompare(b.label)));
        setPermissionsLoaded(true);
        return;
      }

      const processPermissions = new Set(Object.values(constants.process_permissions));
      const fallback = Object.entries(constants.permission_labels)
        .filter(([permission]) => !processPermissions.has(permission))
        .map(([permission, label]) => ({ permission, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setPermissionOptions(fallback);
      setPermissionsLoaded(true);
    };

    loadPermissions();
  }, [apiCall, permissionsLoaded, canView_group]);

  const permissionGroups = useMemo(() => {
    const groups: Record<string, Array<{ permission: string; label: string }>> = {};
    const excludedPrefixes = new Set(['session', 'contenttype', 'token', 'tokenproxy']);
    const excludedPermissions = new Set([
      'add_alegraintegration',
      'add_category',
      'add_contenttype',
      'add_designersprobabilities',
      'add_list',
      'add_listitem',
      'add_logentry',
      'add_measurement',
      'add_pqrsticket',
      'add_pqrsticketlogger',
      'add_processrolepermission',
      'add_processroleuser',
      'add_repositionevidence',
      'add_repositionlog',
      'add_session',
      'add_siigointegration',
      'add_sketchrequestlogger',
      'add_stockpermission',
      'add_stocktransaction',
      'add_stocktransactionorder',
      'add_task',
      'add_token',
      'add_tokenproxy',
      'add_useraudit',
      'add_userlog',
      'add_vendor',
      'change_alegraintegration',
      'change_category',
      'change_contenttype',
      'change_groupotline',
      'change_list',
      'change_listitem',
      'change_logentry',
      'change_measurement',
      'change_pqrsticket',
      'change_pqrsticketlogger',
      'change_processrolepermission',
      'change_processroleuser',
      'change_repositionevidence',
      'change_repositionlog',
      'change_session',
      'change_siigointegration',
      'change_sketchrequestlogger',
      'change_stockpermission',
      'change_stocktransaction',
      'change_stocktransactionorder',
      'change_task',
      'change_token',
      'change_tokenproxy',
      'change_useraudit',
      'change_userlog',
      'change_vendor',
      'delete_alegraintegration',
      'delete_category',
      'delete_company',
      'delete_contenttype',
      'delete_designersprobabilities',
      'delete_list',
      'delete_listitem',
      'delete_logentry',
      'delete_measurement',
      'delete_pqrsticket',
      'delete_pqrsticketlogger',
      'delete_processrolepermission',
      'delete_processroleuser',
      'delete_repositionevidence',
      'delete_repositionlog',
      'delete_session',
      'delete_siigointegration',
      'delete_stockpermission',
      'delete_stocktransaction',
      'delete_stocktransactionorder',
      'delete_task',
      'delete_token',
      'delete_tokenproxy',
      'delete_useraudit',
      'delete_userlog',
      'delete_vendor',
      'view_alegraintegration',
      'view_category',
      'view_contenttype',
      'view_list',
      'view_listitem',
      'view_logentry',
      'view_measurement',
      'view_pqrsticket',
      'view_pqrsticketlogger',
      'view_processrolepermission',
      'view_processroleuser',
      'view_repositionevidence',
      'view_repositionlog',
      'view_session',
      'view_siigointegration',
      'view_sketchrequestlogger',
      'view_stockpermission',
      'view_stocktransaction',
      'view_stocktransactionorder',
      'view_task',
      'view_token',
      'view_tokenproxy',
      'view_userlog',
      'view_vendor',
    ]);
    const sectionLabelMap: Record<string, string> = {
      orders: 'Ordenes',
      orderprocess: 'Procesos de Ordenes',
      orderprocesslog: 'Log de Procesos',
      orderprocessconsecutive: 'Procesos de Consecutivos',
      orderprocessconsecutivelog: 'Log de Procesos de Consecutivos',
      transactions: 'Transacciones',
      sketches: 'Bocetos',
      sketchrequests: 'Solicitudes de Bocetos',
      production: 'Produccion',
      processes: 'Procesos',
      'production processes': 'Procesos de Produccion',
      'production_processes': 'Procesos de Produccion',
      repositions: 'Reposiciones',
    };
    const groupLabelFor = (permission: string, label: string) => {
      if (permission.startsWith('view_menu_')) {
        return 'Menú';
      }
      const cleaned = label.replace(/^(Ver|Crear|Editar|Eliminar)\s+/i, '').trim();
      if (cleaned && cleaned !== label) {
        const mapped = sectionLabelMap[cleaned.toLowerCase()];
        return mapped || cleaned;
      }
      const parts = permission.split('_').slice(1).join(' ');
      if (!parts) return permission;
      const mapped = sectionLabelMap[parts.toLowerCase()] || sectionLabelMap[parts.toLowerCase().replace(/\s+/g, '_')];
      return mapped || parts;
    };
    permissionOptions.forEach((option) => {
      if (excludedPermissions.has(option.permission)) return;
      const domain = option.permission.split('_')[1];
      if (domain && excludedPrefixes.has(domain)) return;
      const groupLabel = groupLabelFor(option.permission, option.label);
      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(option);
    });
    return Object.entries(groups)
      .map(([groupLabel, items]) => ({
        groupLabel,
        items: items.sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
  }, [permissionOptions]);

  const handleTogglePermission = (permission: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permission) ? prev.filter((item) => item !== permission) : [...prev, permission],
    );
  };

  const handleSaveRole = async () => {
    if (newRoleName === '') {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'El nombre del rol es requerido',
          show: true,
        }),
      );
      return;
    }
    dispatch(setLoading(true));
    const data = {
      name: newRoleName,
      permissions: selectedPermissions,
    };
    const response = await apiCall('POST', 'groups/', data, 'Rol creado exitosamente', 'Error al crear el rol');
    dispatch(setLoading(false));
    if (response?.id) {
      dispatch(setGroups([...groups, response]));
      setAddNewForm(false);
      setNewRoleName('');
      setSelectedPermissions([]);
    } else {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'Error al crear el rol',
          show: true,
        }),
      );
    }
  };

  const handleEditRole = (role: GroupType) => {
    setEditForm(true);
    setAddNewForm(false);
    setEditRole(role);
    setSelectedPermissions(role.permissions || []);
  };

  const handleUpdateRole = async () => {
    if (editRole.name === constants.groups.GROUP_ADMINISTRADOR) {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'El rol Administrador tiene acceso total y no puede ser modificado',
          show: true,
        }),
      );
      return;
    }
    if (editRole.name === '') {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'El nombre del rol es requerido',
          show: true,
        }),
      );
      return;
    }
    dispatch(setLoading(true));
    const data = {
      name: editRole.name,
      permissions: selectedPermissions,
    };
    const response = await apiCall(
      'PUT',
      `groups/${editRole.id}/`,
      data,
      'Rol actualizado exitosamente',
      'Error al actualizar el rol',
    );
    dispatch(setLoading(false));
    if (response?.id) {
      const updatedRoles = groups.map((group: GroupType) => (group.id === response.id ? response : group));
      dispatch(setGroups(updatedRoles));
      setEditForm(false);
      setEditRole({} as GroupType);
      setSelectedPermissions([]);
    } else {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'Error al actualizar el rol',
          show: true,
        }),
      );
    }
  };

  const handleDeleteRole = async (id: number) => {
    const role = groups.find((group: GroupType) => group.id === id);
    if (role?.name === constants.groups.GROUP_ADMINISTRADOR) {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'El rol Administrador no puede ser eliminado',
          show: true,
        }),
      );
      return;
    }
    dispatch(setLoading(true));
    const response = await apiCall('DELETE', `groups/${id}/`, null, 'Rol eliminado exitosamente', 'Error al eliminar el rol');
    dispatch(setLoading(false));
    if (response) {
      const updatedRoles = groups.filter((group: GroupType) => group.id !== id);
      dispatch(setGroups(updatedRoles));
    }
  };

  const renderPermissionsForm = () => {
    if (editForm && editRole.name === constants.groups.GROUP_ADMINISTRADOR) {
      return <div style={{ fontWeight: 600 }}>ACCESO TOTAL</div>;
    }
    return (
      <FormControl component="fieldset" variant="standard">
        <FormGroup>
          <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '0.5rem' }}>
            {permissionGroups.map((group) => (
              <div key={group.groupLabel} style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{group.groupLabel}</div>
                {group.items.map((option) => (
                  <FormControlLabel
                    key={option.permission}
                    control={
                      <Switch
                        checked={selectedPermissions.includes(option.permission)}
                        onChange={() => handleTogglePermission(option.permission)}
                        name={option.permission}
                      />
                    }
                    label={option.label || option.permission}
                  />
                ))}
              </div>
            ))}
          </div>
        </FormGroup>
      </FormControl>
    );
  };

  if (!canView_group) {
    return null;
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className="text-right">
          <h2>Roles</h2>
        </Grid>
        <Grid item xs={12} sm={2} className="text-right">
          {canAdd_group && (
            <Button onClick={() => {
              setAddNewForm(true);
              setEditForm(false);
              setSelectedPermissions([]);
            }}>Agregar</Button>
          )}
        </Grid>
      </Grid>
      {addNewForm && canAdd_group && (
        <div>
          <h3>Agregar Nuevo Rol</h3>
          <form>
            <input
              type="text"
              placeholder="Nombre del rol"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              className="settings-input form-control"
            />
            <div>
              <h4>Permisos</h4>
              {renderPermissionsForm()}
            </div>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  onClick={() => {
                    setAddNewForm(false);
                    setSelectedPermissions([]);
                    setNewRoleName('');
                  }}
                >
                  Cancelar
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button onClick={handleSaveRole}>Guardar</Button>
              </Grid>
            </Grid>
          </form>
        </div>
      )}
      {editForm && canChange_group && (
        <div>
          <h3>Editar Rol</h3>
          <form>
            <input
              type="text"
              placeholder="Nombre del rol"
              value={editRole.name || ''}
              onChange={(e) => setEditRole({ ...editRole, name: e.target.value })}
              className="settings-input form-control"
            />
            <div>
              <h4>Permisos</h4>
              {renderPermissionsForm()}
            </div>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  onClick={() => {
                    setEditForm(false);
                    setEditRole({} as GroupType);
                    setSelectedPermissions([]);
                  }}
                >
                  Cancelar
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button onClick={handleUpdateRole}>Guardar Cambios</Button>
              </Grid>
            </Grid>
          </form>
        </div>
      )}
      {groups?.length === 0 && <p>No hay roles registrados</p>}
      <Grid container spacing={2}>
        <div className="table-responsive">
          <TableContainer className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>NOMBRE</b>
                  </TableCell>
                  <TableCell width={150}>
                    <b>ACCIONES</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups?.map((group: GroupType) => (
                  <TableRow key={group.id}>
                    <TableCell>{group.name}</TableCell>
                    <TableCell>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          {canChange_group && (
                            <Button
                              onClick={() => handleEditRole(group)}
                              disabled={group.name === constants.groups.GROUP_ADMINISTRADOR}
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Button>
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          {canDelete_group && (
                            <Button
                              onClick={() => handleDeleteRole(group.id)}
                              disabled={group.name === constants.groups.GROUP_ADMINISTRADOR}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Grid>
    </>
  );
};

export default ViewRoles;
